import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useParams } from "@tanstack/react-router"
import {
	IconCopyOutline18 as Copy,
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
	IconArrowsRotateCenterOutline18 as RefreshCw,
} from "nucleo-ui-outline-18"

import {
	AlertDialog,
	Button,
	Checkbox,
	IconButton,
	Input,
	Loader,
	Tooltip,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { WebhookListRow } from "./shared"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsHeader } from "../../../settings-header"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import { collapseLegacyEvents, WEBHOOK_RESOURCES } from "./shared"

// Standalone route page at /settings/integrations/api/webhooks/:id/edit.
// Loads the webhook row from Zero and shows a save-explicit form.
// "Save" persists and stays; back navigation lives in the layout chrome.
export function WebhookEditPage() {
	const { id } = useParams({ strict: false })
	const [hooks] = useQuery(queries.webhooks())
	const hook = useMemo(
		() => hooks.find((h) => h.id === id) ?? null,
		[hooks, id],
	)

	if (!hook) {
		// Either still loading from Zero, or the row was deleted from
		// another tab. Render a simple back link rather than a hard
		// 404; Zero queries hydrate async so a flash of this is
		// expected on first visit.
		return (
			<>
				<MetaInfo title="Edit webhook" />
				<SettingsHeader title="Edit webhook" subtitle="Loading…" />
				<SettingsContainer>
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				</SettingsContainer>
			</>
		)
	}

	return (
		<>
			<MetaInfo title={`Edit ${hook.label}`} />
			<WebhookEditor hook={hook} />
		</>
	)
}

function WebhookEditor({ hook }: { hook: WebhookListRow }) {
	const ctx = api.useUtils()
	// Draft state; flushes only on Save click. Keeps the page out of
	// the auto-save category that's been confusing in other settings
	// pages here.
	const [label, setLabel] = useState(hook.label)
	const [url, setUrl] = useState(hook.url)
	// Collapse legacy granular events (e.g. "thread.created") into
	// resource wildcards (e.g. "thread.*") for editing. Saving back
	// will overwrite the row with the wildcard form, which the server
	// matcher already accepts.
	const [events, setEvents] = useState<Set<string>>(() =>
		collapseLegacyEvents(hook.events),
	)
	const initialEvents = useMemo(
		() => collapseLegacyEvents(hook.events),
		[hook.events],
	)
	const [revealed, setRevealed] = useState(false)
	const [confirmRotate, setConfirmRotate] = useState(false)

	const updateMutation = api.workspace.updateWebhook.useMutation({
		onSuccess: () => {
			toast.success({ title: "Webhook updated" })
			void ctx.workspace.invalidate()
		},
		onError: (err) => toast.error({ title: err.message }),
	})
	const rotateMutation = api.workspace.regenerateWebhookSecret.useMutation({
		onSuccess: () => {
			toast.success({ title: "Signing secret rotated" })
			void ctx.workspace.invalidate()
			setRevealed(true)
		},
	})

	const toggleEvent = (event: string) => {
		setEvents((prev) => {
			const next = new Set(prev)
			if (next.has(event)) next.delete(event)
			else next.add(event)
			return next
		})
	}

	const dirty = useMemo(() => {
		if (label !== hook.label) return true
		if (url !== hook.url) return true
		if (events.size !== initialEvents.size) return true
		for (const e of initialEvents) if (!events.has(e)) return true
		return false
	}, [label, url, events, initialEvents, hook.label, hook.url])

	const isUrlValid = (() => {
		try {
			const parsed = new URL(url)
			return parsed.protocol === "https:" || parsed.protocol === "http:"
		} catch {
			return false
		}
	})()
	const canSave =
		dirty && label.trim().length > 0 && isUrlValid && events.size > 0

	const save = () => {
		if (!canSave) return
		updateMutation.mutate({
			id: hook.id,
			label,
			url,
			events: Array.from(events),
		})
	}

	const reset = () => {
		setLabel(hook.label)
		setUrl(hook.url)
		setEvents(collapseLegacyEvents(hook.events))
	}

	const secretQuery = api.workspace.webhookSecret.useQuery(
		{ id: hook.id },
		{ retry: false },
	)
	const secret = secretQuery.data?.secret ?? ""
	const masked = "•".repeat(32)

	const copyValue = async (value: string, kind: string) => {
		await navigator.clipboard.writeText(value)
		toast.success({ title: `${kind} copied` })
	}

	return (
		<>
			<SettingsHeader
				title="Edit webhook"
				subtitle="Configure where Productlane sends events for this workspace."
			/>

			<SettingsContainer>
				<div className="flex flex-col gap-5">
					<Input
						name="webhook-label"
						label="Label"
						value={label}
						onChange={(e) => setLabel(e.target.value)}
						suffix={
							<Tooltip label="Copy label">
								<IconButton
									Icon={Copy}
									variant="ghost"
									onClick={() => void copyValue(label, "Label")}
								/>
							</Tooltip>
						}
					/>

					<Input
						name="webhook-url"
						label="URL"
						value={url}
						onChange={(e) => setUrl(e.target.value)}
						errorMessage={
							url && !isUrlValid ? "Must be a valid http(s) URL" : undefined
						}
						suffix={
							<Tooltip label="Copy URL">
								<IconButton
									Icon={Copy}
									variant="ghost"
									onClick={() => void copyValue(url, "URL")}
								/>
							</Tooltip>
						}
					/>

					<Input
						name="webhook-secret"
						label="Signing secret"
						type="text"
						value={revealed && secret ? secret : masked}
						readOnly
						hint="Used to sign HMAC-SHA256 of every payload. Rotating invalidates the old secret immediately."
						suffix={
							<div className="flex items-center gap-1">
								<Tooltip label={revealed ? "Hide secret" : "Reveal secret"}>
									<IconButton
										Icon={revealed ? EyeOff : Eye}
										variant="ghost"
										onClick={() => setRevealed(!revealed)}
									/>
								</Tooltip>
								<Tooltip label="Copy secret">
									<IconButton
										Icon={Copy}
										variant="ghost"
										onClick={() => void copyValue(secret, "Secret")}
										disabled={!secret}
									/>
								</Tooltip>
								<Tooltip label="Rotate secret">
									<IconButton
										Icon={RefreshCw}
										variant="ghost"
										onClick={() => setConfirmRotate(true)}
										disabled={rotateMutation.isLoading}
									/>
								</Tooltip>
							</div>
						}
					/>

					<div className="flex flex-col gap-2">
						<div className="text-sm font-medium">Events</div>
						<div className="grid grid-cols-2 gap-x-6 gap-y-2">
							{WEBHOOK_RESOURCES.map((r) => (
								<Checkbox
									key={r.id}
									size="sm"
									checked={events.has(r.id)}
									onCheckedChange={() => toggleEvent(r.id)}
									label={
										<span className="flex items-center gap-2">
											<span>{r.label}</span>
										</span>
									}
								/>
							))}
						</div>
						{events.size === 0 && (
							<div className="text-xs text-red-600">
								Select at least one resource
							</div>
						)}
					</div>

					<div className="flex w-full items-center justify-end gap-2">
						{dirty && (
							<Button variant="outline" onClick={reset}>
								Reset
							</Button>
						)}
						<Button
							onClick={save}
							isLoading={updateMutation.isLoading}
							disabled={!canSave}
						>
							Save webhook
						</Button>
					</div>
				</div>
			</SettingsContainer>

			<AlertDialog
				isOpen={confirmRotate}
				onOpenChange={setConfirmRotate}
				title="Rotate signing secret?"
				description="The current secret will stop working immediately. Receivers verifying with the old secret will reject events until you update them."
				confirmText="Rotate"
				onCancel={() => setConfirmRotate(false)}
				onConfirm={() => {
					rotateMutation.mutate({ id: hook.id })
					setConfirmRotate(false)
				}}
			/>
		</>
	)
}
